/**
 * Central source of truth for brand & contact details on the React side.
 *
 * The backend owns the real settings in /app/backend/company_settings.py and
 * exposes them via GET /api/company-settings. This module caches the response
 * in memory so every document generator — invoices, revised invoices, indents,
 * dispatches, purchase orders, receipts, QC reports, wastage report, PDFs,
 * WhatsApp/share text, backup filenames — can read a single consistent value
 * synchronously via getCompanySettings().
 *
 * Flow:
 *   1. App.js calls initCompanySettings() once on startup. Request fires once
 *      and the result is cached forever (brand rarely changes mid-session).
 *   2. Any screen that is about to print/share a document just calls
 *      getCompanySettings() — if the fetch hasn't resolved yet it returns
 *      the baked-in defaults, which are the same brand strings. No awaits
 *      bleed into render paths.
 */

// Baked-in defaults MUST match backend/company_settings.py so that any screen
// rendered before the fetch resolves still shows the correct brand.
const DEFAULTS = Object.freeze({
  company_name: "VEZLO",
  company_legal_name: "VEZLO",
  company_tagline: "Fresh Fruits & Vegetables",
  company_address:
    "M.NO. 1638,\nTaleranwadi, Kesnand Taluka - Haveli\nPune - 412207\nMaharashtra",
  company_address_single_line:
    "M.NO. 1638, Taleranwadi, Kesnand Taluka - Haveli, Pune - 412207, Maharashtra",
  company_phone: "9145000250",
  company_email: "mrorganixmushroom@gmail.com",
  company_state: "27-Maharashtra",
  bank_name: "Cosmos Bank Co-Op Bank Limited",
  bank_account: "128100101946",
  bank_ifsc: "COSB0000128",
  account_holder: "VEZLO",
  authorized_signatory: "Ankush K.",
  file_slug: "VEZLO",
});

let CURRENT = { ...DEFAULTS };
let hydrationPromise = null;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Fire a single fetch of /api/company-settings. Safe to call multiple times;
 * only the first call hits the network. Caller does not need to await — the
 * cache is replaced in-place when the response arrives.
 */
export function initCompanySettings() {
  if (hydrationPromise) return hydrationPromise;
  hydrationPromise = fetch(`${API_BASE}/api/company-settings`, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data && typeof data === "object") {
        CURRENT = { ...DEFAULTS, ...data };
      }
      return CURRENT;
    })
    .catch(() => CURRENT);
  return hydrationPromise;
}

/** Synchronous access to the latest cached brand/contact details. */
export function getCompanySettings() {
  return CURRENT;
}

/** Convenience accessor used all over the codebase for the display name. */
export function getCompanyName() {
  return CURRENT.company_name;
}

/** Default export mirrors getCompanySettings() for import ergonomics. */
export default getCompanySettings;
